import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { getStrapiImageURL } from '~/services/strapiServices/image/getStrapiImageService';

const LEVELS = [1, 2, 3, 4];
const SEP = '\u0001';
const PLACEHOLDER = '/static/img/products/rawento-product-placeholder.webp';

const convertVariantLabel = (label) => {
  if (!label) return label;

  return label
    .replace(/oe/g, 'ö')
    .replace(/ae/g, 'ä')
    .replace(/ue/g, 'ü')
    .replace(/sss/g, 'ß');
};

const getVal = (variant, level) => variant.attributes[`variant_value_${level}`] ?? null;

const joinApiPath = (path) => {
  const base = process.env.NEXT_PUBLIC_FRONTEND_URL || '';
  return `${base}${path.startsWith('/') ? '' : '/'}${path}`;
};

const resolveVariantImageValue = (value) => {
  if (!value) return null;

  if (Array.isArray(value)) {
    return resolveVariantImageValue(value[0]);
  }

  if (typeof value === 'string') {
    const str = value.trim();
    if (!str) return null;
    if (/^(https?:)?\/\//i.test(str) || str.startsWith('data:')) return str;
    if (str.startsWith('/')) return joinApiPath(str);
    return `${process.env.NEXT_PUBLIC_FRONTEND_URL}/images/${str}`;
  }

  if (typeof value === 'object') {
    const media = Array.isArray(value.data) ? value.data[0] : value.data ?? value;
    const mediaAttrs = media?.attributes ?? media;
    const rawUrl = mediaAttrs?.formats?.thumbnail?.url || mediaAttrs?.url;

    if (!rawUrl) return null;

    try {
      const helperUrl = getStrapiImageURL({
        data: { attributes: { base_image: value } },
      });
      if (helperUrl) return helperUrl;
    } catch (e) {
    }

    if (/^(https?:)?\/\//i.test(rawUrl)) return rawUrl;
    return joinApiPath(rawUrl);
  }

  return null;
};

const getVariantThumbnailUrl = (variant, level) => {
  if (!variant || !variant.attributes) return PLACEHOLDER;

  const url = resolveVariantImageValue(variant.attributes[`variant_value_${level}_image`]);

  return url || PLACEHOLDER;
};

const shouldShowThumbnails = (variantName) => {
  if (!variantName) return false;
  const name = variantName.toLowerCase();

  return (
    name.includes('farbe') ||
    name.includes('color') ||
    name.includes('design') ||
    name.includes('muster')
  );
};

const sortVariantValues = (values) =>
  values
    .map((value) => ({
      original: value,
      display: String(convertVariantLabel(value) ?? ''),
    }))
    .sort((a, b) => {
      const numA = parseFloat(a.display.match(/^\d+(\.\d+)?/)?.[0]);
      const numB = parseFloat(b.display.match(/^\d+(\.\d+)?/)?.[0]);

      if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
      if (!isNaN(numA)) return -1;
      if (!isNaN(numB)) return 1;

      return a.display.localeCompare(b.display, 'de', { sensitivity: 'base' });
    })
    .map((item) => item.original);

const buildValue1Subgroups = (variants, levels) => {
  const byValue1 = {};
  const restLevels = levels.slice(1);

  variants.forEach((variant) => {
    const val1 = getVal(variant, 1);
    const restKey = restLevels.map((n) => `${getVal(variant, n) ?? ''}`).join(SEP);
    byValue1[val1] = byValue1[val1] || {};
    byValue1[val1][restKey] = byValue1[val1][restKey] || [];
    byValue1[val1][restKey].push(variant);
  });

  const result = {};

  Object.entries(byValue1).forEach(([val1, byRest]) => {
    const maxCount = Math.max(...Object.values(byRest).map((arr) => arr.length));
    const subgroups = Array.from({ length: maxCount }, () => ({}));

    Object.entries(byRest).forEach(([restKey, arr]) => {
      arr.forEach((variant, idx) => {
        subgroups[idx][restKey] = variant;
      });
    });

    result[val1] = subgroups;
  });

  return result;
};

const findSubgroupIndexForVariant = (value1Subgroups, variant, levels) => {
  if (!variant) return { value1: null, subgroupIndex: null };

  const val1 = getVal(variant, 1);
  const subgroups = value1Subgroups[val1] || [];
  const variantId = variant.id != null ? String(variant.id) : null;
  const restKey = levels
    .slice(1)
    .map((n) => `${getVal(variant, n) ?? ''}`)
    .join(SEP);

  let subgroupIndex = subgroups.findIndex((group) => {
    const candidate = group[restKey];
    return candidate && variantId != null && String(candidate.id) === variantId;
  });

  if (subgroupIndex === -1) {
    subgroupIndex = subgroups.findIndex((group) =>
      Object.values(group).some((v) => String(v.id) === variantId)
    );
  }

  return {
    value1: val1,
    subgroupIndex: subgroupIndex === -1 ? 0 : subgroupIndex,
  };
};

const ProductVariantSelector = ({ product, selectedVariant, onVariantChange }) => {
  const variants = product?.attributes?.product_variants?.data || [];
  const levels = LEVELS.filter((n) => !!product?.attributes?.[`variant_name_${n}`]);
  const value1Subgroups = buildValue1Subgroups(variants, levels);

  const [selectedValue1, setSelectedValue1] = useState(null);
  const [selectedSubgroupIndex, setSelectedSubgroupIndex] = useState(0);
  const [selectedValues, setSelectedValues] = useState({});
  const [hoverValues, setHoverValues] = useState({});
  const [failedImages, setFailedImages] = useState({});

  const router = useRouter();

  const getName = (level) => product?.attributes?.[`variant_name_${level}`];
  const isImageGroup = (level) => shouldShowThumbnails(getName(level));

  const values1 = sortVariantValues([...new Set(variants.map((variant) => getVal(variant, 1)))]);

  const value1Buttons = values1.flatMap((val1) =>
    (value1Subgroups[val1] || []).map((subgroup, subgroupIndex) => ({
      value1: val1,
      subgroupIndex,
      representativeVariant: Object.values(subgroup)[0],
    }))
  );

  const getSubgroup = (val1, subgroupIndex) =>
    (value1Subgroups[val1] || [])[subgroupIndex] || {};

  const getCandidates = (val1, subgroupIndex, sel, upToLevel) =>
    Object.values(getSubgroup(val1, subgroupIndex)).filter((variant) =>
      levels
        .slice(1)
        .filter((n) => n < upToLevel)
        .every((n) => getVal(variant, n) === (sel[n] ?? null))
    );

  const getAvailableValues = (val1, subgroupIndex, sel, level) =>
    sortVariantValues([
      ...new Set(
        getCandidates(val1, subgroupIndex, sel, level).map((variant) => getVal(variant, level))
      ),
    ]);

  const resolveSelection = (val1, subgroupIndex, preferred) => {
    const next = {};

    levels.slice(1).forEach((n) => {
      const available = getAvailableValues(val1, subgroupIndex, next, n);
      next[n] = available.includes(preferred[n]) ? preferred[n] : available[0] ?? null;
    });

    return next;
  };

  const resolveAndEmit = (val1, subgroupIndex, sel) => {
    const variant = getCandidates(val1, subgroupIndex, sel, Infinity)[0];
    if (variant) {
      onVariantChange(variant);
    }
  };

  // Stabile Abhängigkeit: nur die ID, nicht das (evtl. bei jedem Render neue) Objekt.
  const selectedVariantId = selectedVariant?.id ?? null;

  useEffect(() => {
    if (!selectedVariant) return;

    const { value1, subgroupIndex } = findSubgroupIndexForVariant(
      value1Subgroups,
      selectedVariant,
      levels
    );

    const sel = {};
    levels.slice(1).forEach((n) => {
      sel[n] = getVal(selectedVariant, n);
    });

    setSelectedValue1(value1);
    setSelectedSubgroupIndex(subgroupIndex);
    // Gleiche Referenz zurückgeben, wenn sich nichts geändert hat -> kein Re-Render
    setSelectedValues((prev) =>
      levels.slice(1).every((n) => prev[n] === sel[n]) ? prev : sel
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVariantId]);

  const handleValue1Change = (val1, subgroupIndex) => {
    const sel = resolveSelection(val1, subgroupIndex, selectedValues);

    setSelectedValue1(val1);
    setSelectedSubgroupIndex(subgroupIndex);
    setSelectedValues(sel);
    resolveAndEmit(val1, subgroupIndex, sel);
  };

  const handleValueChange = (level, value) => {
    const sel = resolveSelection(selectedValue1, selectedSubgroupIndex, {
      ...selectedValues,
      [level]: value,
    });

    setSelectedValues(sel);
    resolveAndEmit(selectedValue1, selectedSubgroupIndex, sel);
  };

  const setHover = (level, value) =>
    setHoverValues((prev) => ({ ...prev, [level]: value }));

  const markImageFailed = (url) =>
    setFailedImages((prev) => (prev[url] ? prev : { ...prev, [url]: true }));

  const getOptions = (level) => {
    if (level === 1) {
      return value1Buttons.map(({ value1, subgroupIndex, representativeVariant }) => ({
        key: `${value1}__${subgroupIndex}`,
        value: value1,
        thumbUrl: getVariantThumbnailUrl(representativeVariant, 1),
        selected: selectedValue1 === value1 && selectedSubgroupIndex === subgroupIndex,
        onClick: () => handleValue1Change(value1, subgroupIndex),
      }));
    }

    const available = getAvailableValues(
      selectedValue1,
      selectedSubgroupIndex,
      selectedValues,
      level
    );
    const candidates = getCandidates(
      selectedValue1,
      selectedSubgroupIndex,
      selectedValues,
      level
    );

    return available.map((value, index) => ({
      key: `${level}__${index}`,
      value,
      thumbUrl: getVariantThumbnailUrl(
        candidates.find((v) => getVal(v, level) === value),
        level
      ),
      selected: selectedValues[level] === value,
      onClick: () => handleValueChange(level, value),
    }));
  };

  const hasValidVariantName1 = !!getName(1);

  const getDisplayValue = (level) =>
    hoverValues[level] || (level === 1 ? selectedValue1 : selectedValues[level]);

  const hasRealImage = (url) =>
    !!url && !url.includes('rawento-product-placeholder.webp') && !failedImages[url];

  return (
    <div className="product-variant-selector">
      <style jsx>{`
        .product-variant-selector {
          margin-top: 12px;
        }
        .variant-options {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }
        .variant-level.has-options {
          margin-bottom: 10px;
        }
        .variant-level.has-options .variant-label {
          margin-bottom: 8px;
        }
        .variant-label {
          margin-bottom: 0;
          font-size: 14px;
          line-height: 20px;
          color: #6b7280;
        }
        .variant-label strong {
          font-weight: 600;
          color: #1a1a1a;
        }
        .variant-label-value {
          color: #1a1a1a;
          font-weight: 400;
        }

        .option-buttons {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
          align-items: center;
          padding: 4px;
          margin: -4px;
        }

        /* Runde Farb-/Design-Swatches */
        .option-swatch {
          flex: 0 0 auto;
          width: 44px;
          height: 44px;
          padding: 0;
          border-radius: 50%;
          border: 1px solid rgba(0, 0, 0, 0.15);
          background: #e9ecef;
          cursor: pointer;
          transition: outline-color 0.15s ease;
        }
        .option-swatch img {
          display: block;
          width: 100%;
          height: 100%;
          border-radius: 50%;
          object-fit: cover;
          pointer-events: none;
          -webkit-user-drag: none;
          user-select: none;
        }
        .option-swatch:hover {
          outline: 2px solid #9bb3d4;
          outline-offset: 3px;
        }
        .option-swatch.selected {
          outline: 2px solid #1c3f73;
          outline-offset: 3px;
        }
        .option-swatch:focus-visible {
          outline: 2px solid #1c3f73;
          outline-offset: 3px;
        }

        /* Text-Chips (Größe, Material, ...) */
        .option-chip {
          flex: 0 1 auto;
          min-width: 44px;
          height: 38px;
          padding: 0 16px;
          border-radius: 19px;
          border: 1px solid #d4d4d8;
          background: #fff;
          font-size: 13px;
          color: #1a1a1a;
          white-space: nowrap;
          cursor: pointer;
          transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
        }
        .option-chip:hover {
          border-color: #1c3f73;
          background: #e3f2fd;
        }
        .option-chip.selected {
          background: #1c3f73;
          border-color: #1c3f73;
          color: #fff;
        }
        .option-chip:focus-visible {
          outline: 2px solid #1c3f73;
          outline-offset: 2px;
        }
      `}</style>

      {hasValidVariantName1 && (
        <div className="variant-options">
          {levels.map((level) => {
            if (level > 1 && !selectedValue1) return null;

            const options = getOptions(level);
            const asSwatches =
              isImageGroup(level) &&
              options.length > 0 &&
              options.every((o) => hasRealImage(o.thumbUrl));
            const displayValue = getDisplayValue(level);
            const hasOptions = options.length > 0;

            return (
              <div
                key={`level-${level}`}
                className={`variant-level ${hasOptions ? 'has-options' : ''}`}
              >
                <div className="variant-label">
                  <strong>{convertVariantLabel(getName(level))}</strong>{' '}
                  <span className="variant-label-value">
                    {displayValue ? convertVariantLabel(displayValue) : ''}
                  </span>
                </div>

                {hasOptions && (
                <div className="option-buttons">
                  {options.map(({ key, value, thumbUrl, selected, onClick }) => {
                    const label = convertVariantLabel(value);

                    return asSwatches ? (
                      <button
                        key={key}
                        type="button"
                        title={label}
                        aria-label={label}
                        aria-pressed={selected}
                        onClick={onClick}
                        onMouseEnter={() => setHover(level, value)}
                        onMouseLeave={() => setHover(level, null)}
                        className={`option-swatch ${selected ? 'selected' : ''}`}
                      >
                        <img
                          src={thumbUrl}
                          alt=""
                          draggable={false}
                          onError={() => markImageFailed(thumbUrl)}
                        />
                      </button>
                    ) : (
                      <button
                        key={key}
                        type="button"
                        aria-pressed={selected}
                        onClick={onClick}
                        onMouseEnter={() => setHover(level, value)}
                        onMouseLeave={() => setHover(level, null)}
                        className={`option-chip ${selected ? 'selected' : ''}`}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ProductVariantSelector;